import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ImportTab from './components/ImportTab';

const TABS = [
  { key: 'sites', label: 'Sites', icon: 'Building2', description: 'Import site records with client and project details' },
  { key: 'users', label: 'Users', icon: 'Users', description: 'Bulk invite admins, site managers, and workers via CSV' },
  { key: 'workers', label: 'Workers', icon: 'HardHat', description: 'Import worker profiles with trade qualifications' },
  { key: 'tasks', label: 'Tasks', icon: 'ClipboardList', description: 'Bulk create daily tasks linked to existing sites' },
  { key: 'certifications', label: 'Certifications', icon: 'Award', description: 'Import worker certifications linked by worker email' },
  { key: 'documents', label: 'Document Records', icon: 'FileText', description: 'Import document records (metadata only) linked to existing sites — does not upload files' },
  { key: 'snags', label: 'Snags', icon: 'AlertTriangle', description: 'Import snag records linked to existing sites' },
];

const FIELD_HINTS = {
  sites: <><strong>Required:</strong> site_name, address_line1, city, postcode, client_name, start_date, end_date &nbsp;|&nbsp; <strong>Status:</strong> planning, active, on_hold, completed, archived &nbsp;|&nbsp; <strong>Dates:</strong> YYYY-MM-DD or DD-MM-YYYY</>,
  users: <><strong>Required:</strong> full_name, email, role &nbsp;|&nbsp; <strong>Role:</strong> admin, site_manager, worker &nbsp;|&nbsp; <strong>Max:</strong> 100 rows per upload &nbsp;|&nbsp; Each user receives an invite email to set their password</>,
  workers: <><strong>Required:</strong> full_name, email &nbsp;|&nbsp; <strong>Dates:</strong> YYYY-MM-DD or DD-MM-YYYY &nbsp;|&nbsp; Each worker receives an invite email</>,
  tasks: <><strong>Required:</strong> title, site_name (must match existing site), task_date &nbsp;|&nbsp; <strong>Status:</strong> pending, in_progress, completed, blocked &nbsp;|&nbsp; <strong>Category:</strong> Safety, Operations, Compliance, Maintenance, Inspection, Admin</>,
  certifications: <><strong>Required:</strong> workerEmail (must match existing worker), certificationType, expiryDate &nbsp;|&nbsp; <strong>Dates:</strong> YYYY-MM-DD or DD-MM-YYYY &nbsp;|&nbsp; <strong>issuedDate:</strong> optional</>,
  documents: <><strong>Records metadata only</strong> — creates document records (name, category, expiry) for tracking; it does not upload the files themselves. &nbsp;|&nbsp; <strong>Required:</strong> documentName, category, siteName (must match existing site), expiryDate, owner &nbsp;|&nbsp; <strong>Optional:</strong> issueDate (defaults to today) &nbsp;|&nbsp; <strong>Category:</strong> rams, insurance, training, plant, inspection_report, subcontractor, equipment_certificate &nbsp;|&nbsp; <strong>Dates:</strong> YYYY-MM-DD or DD-MM-YYYY</>,
  snags: <><strong>Required:</strong> siteName (must match existing site), description &nbsp;|&nbsp; <strong>Priority:</strong> low, medium, high, critical &nbsp;|&nbsp; <strong>Status:</strong> open, in_progress, resolved, completed &nbsp;|&nbsp; <strong>Dates:</strong> YYYY-MM-DD or DD-MM-YYYY</>,
};

const CsvImportCenter = () => {
  const [activeTab, setActiveTab] = useState('sites');
  const [toasts, setToasts] = useState([]);

  const handleImportSuccess = ({ type, imported, skipped }) => {
    const id = Date.now();
    const label = TABS?.find(t => t?.key === type)?.label || type;
    setToasts(prev => [
      ...prev,
      { id, message: `${label}: ${imported} imported${skipped > 0 ? `, ${skipped} skipped` : ''}` },
    ]);
    setTimeout(() => setToasts(prev => prev?.filter(t => t?.id !== id)), 5000);
  };

  const activeTabData = TABS?.find(t => t?.key === activeTab);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-20 pb-8">
        {/* Page heading */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2 bg-accent/10 rounded-lg">
            <Icon name="FileSpreadsheet" size={22} className="text-accent" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text-primary">CSV Import Center</h1>
            <p className="text-sm text-text-secondary">Bulk import sites, workers, tasks, certifications, document records, and snags from CSV files</p>
          </div>
        </div>

        {/* Tab navigation — two rows on small screens */}
        <div className="flex flex-wrap gap-1 bg-surface border border-border rounded-xl p-1 mb-8">
          {TABS?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab?.key
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary hover:bg-background'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Tab content card */}
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-border bg-background/50">
            <div className="flex items-center gap-3">
              <Icon name={activeTabData?.icon} size={20} className="text-accent" />
              <div>
                <h2 className="text-base font-semibold text-text-primary">Import {activeTabData?.label}</h2>
                <p className="text-sm text-text-secondary">{activeTabData?.description}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Field reference */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={15} className="text-blue-600 mt-0.5 shrink-0" />
                <div className="text-xs text-blue-700">
                  {FIELD_HINTS?.[activeTab]}
                </div>
              </div>
            </div>

            <ImportTab
              key={activeTab}
              type={activeTab}
              onImportSuccess={handleImportSuccess}
            />
          </div>
        </div>
      </div>

      {/* Toast notifications */}
      <div className="fixed bottom-6 right-6 space-y-2 z-50">
        {toasts?.map(toast => (
          <div
            key={toast?.id}
            className="flex items-center gap-3 bg-text-primary text-white px-4 py-3 rounded-xl shadow-lg text-sm font-medium animate-fade-in"
          >
            <Icon name="CheckCircle" size={16} className="text-success shrink-0" />
            {toast?.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CsvImportCenter;
